import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Save, Tag, Search, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const FEELINGS = [
  { key: 'sad', label: 'Sad', emoji: '💧' },
  { key: 'anxious', label: 'Anxious', emoji: '🌫' },
  { key: 'angry', label: 'Angry', emoji: '🔥' },
  { key: 'confused', label: 'Confused', emoji: '🤔' },
  { key: 'hopeful', label: 'Hopeful', emoji: '☀️' },
  { key: 'hurt', label: 'Hurt', emoji: '💔' },
];
const RELATIONSHIPS = [
  { key: 'spouse', label: 'Spouse', emoji: '💍' },
  { key: 'family', label: 'Family', emoji: '🏡' },
  { key: 'friend', label: 'Friend', emoji: '🤝' },
  { key: 'child', label: 'Child', emoji: '🌱' },
  { key: 'myself', label: 'Myself', emoji: '🪞' },
];

/**
 * Bulk Article Tagging — quickly tag every article with feelings + relationships
 * so the Journey wizard has plenty of content to serve.
 */
export default function AdminBulkTagging() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});   // id -> true
  const [q, setQ] = useState('');
  const [showOnly, setShowOnly] = useState('all'); // all | untagged | tagged

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/articles?status=all');
      setArticles((data || []).map(a => ({ ...a, feelings: a.feelings || [], relationships: a.relationships || [] })));
    } catch (_) {
      toast.error('Could not load articles');
    } finally { setLoading(false); }
  };

  const toggle = (articleId, kind, key) => {
    setArticles(prev => prev.map(a => {
      if (a.id !== articleId) return a;
      const list = a[kind] || [];
      const has = list.includes(key);
      return { ...a, [kind]: has ? list.filter(x => x !== key) : [...list, key] };
    }));
    setDirty(prev => ({ ...prev, [articleId]: true }));
  };

  const saveAll = async () => {
    const rows = articles
      .filter(a => dirty[a.id])
      .map(a => ({ id: a.id, feelings: a.feelings, relationships: a.relationships }));
    if (rows.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/admin/articles/bulk-tags', { rows });
      toast.success(`Saved ${data.updated} article${data.updated === 1 ? '' : 's'}`);
      setDirty({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    let list = articles;
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(a => (a.title || '').toLowerCase().includes(needle));
    }
    if (showOnly === 'untagged') list = list.filter(a => !(a.feelings?.length) && !(a.relationships?.length));
    if (showOnly === 'tagged') list = list.filter(a => (a.feelings?.length) || (a.relationships?.length));
    return list;
  }, [articles, q, showOnly]);

  const untaggedCount = articles.filter(a => !(a.feelings?.length) && !(a.relationships?.length)).length;
  const dirtyCount = Object.keys(dirty).length;

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="bulktag-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#B77BB020' }}>
              <Tag className="w-6 h-6" style={{ color: '#B77BB0' }} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#B77BB0', letterSpacing: '0.15em' }}>Journey</p>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Bulk Tag Articles</h1>
            </div>
          </div>
          <button
            onClick={saveAll}
            disabled={saving || dirtyCount === 0}
            className="px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-50 transition hover:-translate-y-0.5"
            style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #E57A44 0%, #D96A34 100%)', color: '#FDFBF7', fontWeight: 700 }}
            data-testid="bulktag-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : dirtyCount > 0 ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save changes'}
          </button>
        </div>
        <p className="mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Tag every article with the feelings & relationships it speaks to. The Journey wizard uses these tags to know what to show. <b>{untaggedCount}</b> article{untaggedCount === 1 ? '' : 's'} untagged.</p>

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#8C867D' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search article titles…"
              className="w-full pl-10 pr-4 py-2.5 rounded-full border"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2724', fontSize: '14px' }}
              data-testid="bulktag-search"
            />
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-full" style={{ backgroundColor: '#F3EFE7' }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'untagged', label: `Untagged (${untaggedCount})` },
              { key: 'tagged', label: 'Tagged' },
            ].map(t => (
              <button key={t.key} onClick={() => setShowOnly(t.key)}
                className="px-3 py-1.5 rounded-full text-xs transition"
                style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, backgroundColor: showOnly === t.key ? '#2C2724' : 'transparent', color: showOnly === t.key ? '#FDFBF7' : '#595553' }}
                data-testid={`bulktag-filter-${t.key}`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: '#B77BB0' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="bulktag-empty">
            <p style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>No articles match your filters.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {filtered.map(a => (
              <li key={a.id} className="p-4 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: dirty[a.id] ? '1.5px solid #E57A44' : '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }} data-testid={`bulktag-row-${a.id}`}>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <p className="font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: '15px' }}>{a.title}</p>
                    <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{a.category} · {a.status}</p>
                  </div>
                  {dirty[a.id] && (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFF5E5', color: '#B34E1F', fontWeight: 700 }}>Unsaved</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {FEELINGS.map(f => {
                    const on = a.feelings.includes(f.key);
                    return (
                      <button key={f.key} onClick={() => toggle(a.id, 'feelings', f.key)}
                        className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1 transition"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: on ? '#E57A44' : '#FDFBF7', color: on ? '#FFF' : '#595553', border: `1px solid ${on ? '#E57A44' : '#E6E2D8'}`, fontWeight: 700 }}
                        data-testid={`bulktag-${a.id}-feeling-${f.key}`}
                      >
                        {on && <Check className="w-3 h-3" />}<span>{f.emoji}</span> {f.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {RELATIONSHIPS.map(r => {
                    const on = a.relationships.includes(r.key);
                    return (
                      <button key={r.key} onClick={() => toggle(a.id, 'relationships', r.key)}
                        className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1 transition"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: on ? '#78A18E' : '#FDFBF7', color: on ? '#FFF' : '#595553', border: `1px solid ${on ? '#78A18E' : '#E6E2D8'}`, fontWeight: 700 }}
                        data-testid={`bulktag-${a.id}-relationship-${r.key}`}
                      >
                        {on && <Check className="w-3 h-3" />}<span>{r.emoji}</span> {r.label}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Sticky save reminder when there are unsaved changes */}
        {dirtyCount > 0 && !saving && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-5 py-3 rounded-full flex items-center gap-3 z-50" style={{ background: 'linear-gradient(135deg, #2C2724 0%, #4A3F35 100%)', color: '#FDFBF7', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }} data-testid="bulktag-sticky-save">
            <span className="text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>You have <b>{dirtyCount}</b> unsaved change{dirtyCount === 1 ? '' : 's'}</span>
            <button onClick={saveAll} className="px-4 py-1.5 rounded-full text-sm flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif', background: '#E57A44', color: '#FDFBF7', fontWeight: 700 }} data-testid="bulktag-sticky-save-btn">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
